// client/src/pages/admin/ManageStorySubcategories.js

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import storyService from '../../services/storyService.js';

export default function ManageStorySubcategories() {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const emptyForm = { categoryid: '', subcategoryname: '', description: '', displayorder: 0, isactive: true };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subRes, catRes] = await Promise.all([
        storyService.adminGetSubcategories(),
        storyService.getCategories()
      ]);
      setSubcategories(subRes.subcategories || []);
      setCategories(catRes.categories || []);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sub) => {
    setForm({
      categoryid: sub.categoryid,
      subcategoryname: sub.subcategoryname,
      description: sub.description || '',
      displayorder: sub.displayorder || 0,
      isactive: sub.isactive
    });
    setEditing(sub.subcategoryid);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleNew = () => {
    setForm(emptyForm);
    setEditing(null);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.categoryid || !form.subcategoryname) {
      setError('Category and name are required');
      return;
    }
    try {
      setError('');
      if (editing) {
        await storyService.adminUpdateSubcategory(editing, form);
        setSuccess('Subcategory updated');
      } else {
        await storyService.adminCreateSubcategory(form);
        setSuccess('Subcategory created');
      }
      handleCancel();
      loadData();
    } catch (err) {
      setError('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await storyService.adminDeleteSubcategory(id);
      setSubcategories((prev) => prev.filter((s) => s.subcategoryid !== id));
      setDeleteConfirm(null);
      setSuccess('Subcategory deleted');
    } catch (err) {
      setError('Failed to delete — it may have stories assigned to it');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-gold mb-1">Story Subcategories</h1>
          <p className="text-royal-200 text-base">{subcategories.length} subcategories</p>
        </div>
        <button
          onClick={handleNew}
          className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
        >
          + New Subcategory
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-base mb-4">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-royal-800 border border-gold/30 rounded-lg text-gold text-base mb-4">{success}</div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6 mb-6">
          <h2 className="text-white text-base font-semibold mb-4">
            {editing ? 'Edit Subcategory' : 'New Subcategory'}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-royal-200 text-sm block mb-1">Parent Category *</label>
                <select
                  value={form.categoryid}
                  onChange={(e) => setForm((p) => ({ ...p, categoryid: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold transition-colors"
                >
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c.categoryid} value={c.categoryid}>{c.categoryname}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-royal-200 text-sm block mb-1">Name *</label>
                <input
                  value={form.subcategoryname}
                  onChange={(e) => setForm((p) => ({ ...p, subcategoryname: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors"
                  placeholder="Subcategory name"
                />
              </div>
            </div>
            <div>
              <label className="text-royal-200 text-sm block mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors resize-y"
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-royal-200 text-sm block mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.displayorder}
                  onChange={(e) => setForm((p) => ({ ...p, displayorder: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold transition-colors"
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isactive}
                    onChange={(e) => setForm((p) => ({ ...p, isactive: e.target.checked }))}
                    className="w-5 h-5 rounded border border-royal-600 accent-gold"
                  />
                  <span className="text-white text-base">Active</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-royal-600 text-royal-200 rounded-lg text-sm hover:border-gold/50 hover:text-gold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors"
              >
                {editing ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-royal-700/50">
              <th className="text-left px-4 py-3 text-base text-royal-200 font-medium">Name</th>
              <th className="text-left px-4 py-3 text-base text-royal-200 font-medium hidden md:table-cell">Category</th>
              <th className="text-center px-4 py-3 text-base text-royal-200 font-medium">Order</th>
              <th className="text-center px-4 py-3 text-base text-royal-200 font-medium">Status</th>
              <th className="text-right px-4 py-3 text-base text-royal-200 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subcategories.map((sub) => (
              <tr key={sub.subcategoryid} className="border-b border-royal-800/40 hover:bg-royal-800/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="text-white text-base font-medium">{sub.subcategoryname}</div>
                  {sub.description && <div className="text-royal-200 text-sm mt-0.5 line-clamp-1">{sub.description}</div>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="text-royal-200 text-sm">{sub.categoryname}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-royal-200 text-sm">{sub.displayorder}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-sm px-2 py-0.5 rounded ${
                    sub.isactive ? 'bg-green-500/20 text-green-400' : 'bg-royal-800 text-royal-200'
                  }`}>
                    {sub.isactive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => handleEdit(sub)}
                      className="text-gold hover:text-gold-light text-sm transition-colors"
                    >
                      Edit
                    </button>
                    {deleteConfirm === sub.subcategoryid ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(sub.subcategoryid)}
                          className="text-red-400 hover:text-red-300 text-sm transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-royal-200 hover:text-white text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(sub.subcategoryid)}
                        className="text-red-400 hover:text-red-300 text-sm transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {subcategories.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-royal-200 text-base">
                  No subcategories yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}